import { useState, useRef, useMemo, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAppStore } from '../store/useAppStore.js';
import { useToastStore } from '../store/useToastStore.js';
import { useFilterStore, applyFilters } from '../store/useFilterStore.js';
import { KanbanBoard } from '../components/board/KanbanBoard.jsx';
import { MobileBoard } from '../components/board/MobileBoard.jsx';
import { ListView } from '../components/board/ListView.jsx';
import { ApplicationModal } from '../components/board/ApplicationModal.jsx';
import { StatusMenu } from '../components/board/StatusMenu.jsx';
import { SearchBar } from '../components/common/SearchBar.jsx';
import { FilterPanel } from '../components/common/FilterPanel.jsx';
import { exportToCsv } from '../utils/exportCsv.js';
import { useKeyboardShortcuts } from '../utils/useKeyboardShortcuts.js';
import { updateStatus } from '../api/client.js';
import { STATUS_ORDER, STATUS_LABELS, NETWORKING_STATUS_ORDER, NETWORKING_STATUS_LABELS } from '@launchpad/shared';

export function Board() {
  const [searchParams, setSearchParams] = useSearchParams();
  const applications = useAppStore((s) => s.applications);
  const filters = useFilterStore();
  const [boardType, setBoardType] = useState(() => searchParams.get('boardType') || localStorage.getItem('boardType') || 'application');
  const boardFiltered = useMemo(() => applications.filter((app) =>
    boardType === 'networking' ? app.boardType === 'networking' : !app.boardType || app.boardType === 'application'
  ), [applications, boardType]);
  const filtered = useMemo(() => applyFilters(boardFiltered, filters), [boardFiltered, filters]);
  const [selectedApp, setSelectedApp] = useState(null);

  // Apply URL params on mount (from goal navigation)
  useEffect(() => {
    const dateFrom = searchParams.get('dateFrom');
    if (dateFrom) {
      useFilterStore.getState().setDateRange({ from: dateFrom, to: '' });
      setSearchParams({}, { replace: true });
    }
  }, []);
  const [showModal, setShowModal] = useState(false);
  const [view, setView] = useState(() => localStorage.getItem('boardView') || 'board');
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [statusMenu, setStatusMenu] = useState(null); // { app, position }
  const searchRef = useRef(null);
  const updateApplicationLocal = useAppStore((s) => s.updateApplicationLocal);
  const addToast = useToastStore((s) => s.addToast);

  function toggleView(v) {
    setView(v);
    localStorage.setItem('boardView', v);
  }

  function toggleBoardType(t) {
    setBoardType(t);
    localStorage.setItem('boardType', t);
    // Disposition is application-only; clear the filter when leaving the app board
    if (t === 'networking' && filters.dispositionFilter.length) {
      useFilterStore.getState().setDispositionFilter([]);
    }
  }

  function handleCardClick(app) {
    setSelectedApp(app);
    setShowModal(true);
  }

  function handleCreate() {
    setSelectedApp(null);
    setShowModal(true);
  }

  function handleClose() {
    setShowModal(false);
    setSelectedApp(null);
  }

  function handleContextMenu(app, position) {
    setStatusMenu({ app, position });
  }

  async function handleStatusSelect(newStatus) {
    const { app } = statusMenu;
    setStatusMenu(null);
    if (newStatus === app.status) return;

    const prevStatus = app.status;
    updateApplicationLocal(app.appId, { status: newStatus });

    try {
      await updateStatus(app.appId, newStatus);
      addToast(`Moved to ${newStatus} ✓`, 'success');
    } catch {
      updateApplicationLocal(app.appId, { status: prevStatus });
      addToast('Status change failed', 'error');
    }
  }

  const shortcuts = useMemo(() => ({
    'n': () => handleCreate(),
    '/': () => searchRef.current?.focus(),
    'v': () => toggleView(view === 'board' ? 'list' : 'board'),
    '?': () => setShowShortcuts((s) => !s),
    'Escape': () => {
      if (statusMenu) setStatusMenu(null);
      else if (showModal) handleClose();
      else if (showShortcuts) setShowShortcuts(false);
      else searchRef.current?.blur();
    },
  }), [view, filtered, showModal, showShortcuts, statusMenu]);

  useKeyboardShortcuts(shortcuts);

  const hasFilters = filters.search || filters.statusFilter.length || filters.tagFilter.length || filters.priorityFilter.length || filters.dispositionFilter.length || filters.dateRange.from || filters.dateRange.to;

  return (
    <div className="h-full flex flex-col">
      <header className="flex flex-col gap-2 border-b border-slate-200 px-4 py-3">
        {/* Mobile header */}
        <div className="flex items-center justify-between sm:hidden">
          <div className="flex items-center gap-2">
            <h1 className="text-lg font-semibold text-slate-900">Board</h1>
            <div className="flex rounded border border-slate-200 text-xs">
              <button
                onClick={() => toggleBoardType('networking')}
                className={`px-2 py-1 ${boardType === 'networking' ? 'bg-primary-50 text-primary-700 font-medium' : 'text-slate-600'}`}
              >
                Network
              </button>
              <button
                onClick={() => toggleBoardType('application')}
                className={`px-2 py-1 ${boardType === 'application' ? 'bg-primary-50 text-primary-700 font-medium' : 'text-slate-600'}`}
              >
                Apps
              </button>
            </div>
          </div>
          <button
            onClick={handleCreate}
            className="rounded bg-primary-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-700"
          >
            + New
          </button>
        </div>
        <div className="sm:hidden">
          <SearchBar ref={searchRef} />
        </div>

        {/* Desktop header */}
        <div className="hidden sm:flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h1 className="text-lg font-semibold text-slate-900">Board</h1>
            <div className="flex rounded border border-slate-200 text-xs">
              <button
                onClick={() => toggleBoardType('networking')}
                className={`px-3 py-1 ${boardType === 'networking' ? 'bg-primary-50 text-primary-700 font-medium' : 'text-slate-600 hover:bg-slate-50'}`}
              >
                Networking
              </button>
              <button
                onClick={() => toggleBoardType('application')}
                className={`px-3 py-1 ${boardType === 'application' ? 'bg-primary-50 text-primary-700 font-medium' : 'text-slate-600 hover:bg-slate-50'}`}
              >
                Applications
              </button>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <SearchBar ref={searchRef} />
            <div className="flex rounded border border-slate-200 text-xs">
              <button
                onClick={() => toggleView('board')}
                className={`px-3 py-1 ${view === 'board' ? 'bg-primary-50 text-primary-700 font-medium' : 'text-slate-600 hover:bg-slate-50'}`}
              >
                Board
              </button>
              <button
                onClick={() => toggleView('list')}
                className={`px-3 py-1 ${view === 'list' ? 'bg-primary-50 text-primary-700 font-medium' : 'text-slate-600 hover:bg-slate-50'}`}
              >
                List
              </button>
            </div>
            <button
              onClick={handleCreate}
              className="rounded bg-primary-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-700"
            >
              + New
            </button>
          </div>
        </div>

        <FilterPanel boardType={boardType} />

        {hasFilters && (
          <p className="text-xs text-slate-500">{filtered.length} matching application{filtered.length !== 1 ? 's' : ''}</p>
        )}
      </header>

      <div className="flex-1 overflow-hidden">
        {/* Mobile: always show MobileBoard (no drag-and-drop on touch) */}
        <div className="sm:hidden h-full">
          <MobileBoard applications={filtered} onCardClick={handleCardClick} onContextMenu={handleContextMenu} statusOrder={boardType === 'networking' ? NETWORKING_STATUS_ORDER : STATUS_ORDER} statusLabels={boardType === 'networking' ? NETWORKING_STATUS_LABELS : STATUS_LABELS} />
        </div>

        {/* Desktop: Board or List view */}
        <div className="hidden sm:block h-full">
          {view === 'board' ? (
            <KanbanBoard
              applications={filtered}
              onCardClick={handleCardClick}
              onContextMenu={handleContextMenu}
              statusOrder={boardType === 'networking' ? NETWORKING_STATUS_ORDER : STATUS_ORDER}
              statusLabels={boardType === 'networking' ? NETWORKING_STATUS_LABELS : STATUS_LABELS}
            />
          ) : (
            <ListView applications={filtered} onCardClick={handleCardClick} />
          )}
        </div>
      </div>

      {showModal && <ApplicationModal app={selectedApp} boardType={boardType} onClose={handleClose} />}

      {/* Keyboard shortcuts help */}
      {showShortcuts && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setShowShortcuts(false)}>
          <div className="bg-white rounded-lg shadow-lg p-6 max-w-xs w-full" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-sm font-semibold text-slate-900 mb-3">Keyboard Shortcuts</h2>
            <dl className="space-y-2 text-sm">
              {[
                ['n', boardType === 'networking' ? 'New company' : 'New application'],
                ['/', 'Focus search'],
                ['v', 'Toggle board/list view'],
                ['?', 'Show/hide shortcuts'],
                ['Esc', 'Close modal / blur'],
              ].map(([key, desc]) => (
                <div key={key} className="flex justify-between">
                  <dd className="text-slate-600">{desc}</dd>
                  <dt><kbd className="rounded border border-slate-300 bg-slate-50 px-1.5 py-0.5 text-xs font-mono">{key}</kbd></dt>
                </div>
              ))}
            </dl>
          </div>
        </div>
      )}

      {/* Subtle hint — desktop only */}
      {!showShortcuts && !showModal && (
        <button
          onClick={() => setShowShortcuts(true)}
          className="fixed bottom-4 right-4 hidden sm:flex items-center gap-1 rounded border border-slate-200 bg-white px-2 py-1 text-xs text-slate-400 shadow-sm hover:text-slate-600"
        >
          <kbd className="font-mono">?</kbd> Shortcuts
        </button>
      )}

      {statusMenu && (
        <StatusMenu
          currentStatus={statusMenu.app.status}
          position={statusMenu.position}
          onSelect={handleStatusSelect}
          onClose={() => setStatusMenu(null)}
          statusOrder={boardType === 'networking' ? NETWORKING_STATUS_ORDER : STATUS_ORDER}
          statusLabels={boardType === 'networking' ? NETWORKING_STATUS_LABELS : STATUS_LABELS}
        />
      )}
    </div>
  );
}
